import { useEffect } from "react";
import { isHlsUrl } from "../ReusableFunctions/hlsSource";

// Attach an HLS stream URL to a media element and seek to `startSeconds`:
//   - Safari / iOS play HLS natively (set src directly).
//   - Other browsers need hls.js, lazy-loaded so it stays out of the main bundle
//     and only downloads when a class actually has a stream URL.
// No-op when there is no stream URL — the player then renders its direct <src>
// (the existing full-file fallback).
export default function useHlsMedia(mediaRef, streamUrl, startSeconds = 0) {
  useEffect(() => {
    const el = mediaRef.current;
    if (!el || !streamUrl) return undefined;

    const seek = () => {
      if (startSeconds > 0) {
        try {
          el.currentTime = startSeconds;
        } catch (_) {
          /* element not ready to seek — ignore */
        }
      }
    };

    const attachNative = (element) => {
      element.src = streamUrl;
      element.addEventListener("loadedmetadata", seek, { once: true });
    };

    // Non-HLS stream URL (rare) or native HLS support (Safari/iOS): point the
    // element straight at it.
    if (
      !isHlsUrl(streamUrl) ||
      (el.canPlayType && el.canPlayType("application/vnd.apple.mpegurl"))
    ) {
      attachNative(el);
      return () => el.removeEventListener("loadedmetadata", seek);
    }

    // hls.js path (Chrome / Firefox / Edge).
    let hls;
    let cancelled = false;
    import("hls.js")
      .then(({ default: Hls }) => {
        const current = mediaRef.current;
        if (cancelled || !current) return;
        if (Hls.isSupported()) {
          hls = new Hls();
          hls.loadSource(streamUrl);
          hls.attachMedia(current);
          hls.on(Hls.Events.MANIFEST_PARSED, seek);
        } else {
          attachNative(current);
        }
      })
      .catch(() => {
        // hls.js failed to load — last-resort direct attach.
        if (!cancelled && mediaRef.current) attachNative(mediaRef.current);
      });

    return () => {
      cancelled = true;
      if (hls) hls.destroy();
    };
  }, [mediaRef, streamUrl, startSeconds]);
}
